// src/hooks/useAlertWebSocket.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { WS_CONFIG } from '../config/api';

const useAlertWebSocket = (onConditionUpdate, onAlert) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  
  // Store callbacks in refs to avoid dependency issues
  const onConditionUpdateRef = useRef(onConditionUpdate);
  const onAlertRef = useRef(onAlert);
  
  // Update refs when callbacks change
  useEffect(() => {
    onConditionUpdateRef.current = onConditionUpdate;
    onAlertRef.current = onAlert;
  }, [onConditionUpdate, onAlert]);

  const connect = useCallback(() => {
    // Don't connect if component is unmounted
    if (!mountedRef.current) {
      console.log('⚠️ Component unmounted, skipping connection');
      return;
    }

    // Don't connect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('⚠️ Already connected, skipping');
      return;
    }

    try {
      console.log('🔌 Connecting to WebSocket:', WS_CONFIG.URL);
      
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(WS_CONFIG.URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        
        console.log('✅ WebSocket Connected');
        setIsConnected(true);
        setReconnectAttempt(0);
        
        // Start ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_CONFIG.PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message received:', data);
          
          setLastMessage(data);

          switch (data.type) {
            case 'connection_established':
              console.log('✅ Connection established:', data.message);
              break;

            case 'pong':
              console.log('🏓 Pong received');
              break;

            case 'condition_update':
              console.log('📊 Condition update:', data);
              if (onConditionUpdateRef.current) {
                onConditionUpdateRef.current(data);
              }
              break;

            case 'alert_triggered':
              console.log('🔔 Alert triggered:', data);
              if (onAlertRef.current) {
                onAlertRef.current(data);
              }
              break;

            default:
              console.log('❓ Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
          console.log('Raw message:', event.data);
        }
      };

      ws.onerror = (error) => {
        if (!mountedRef.current) return;
        console.error('❌ WebSocket error:', error);
        setIsConnected(false);
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Only reconnect if not a normal closure and we haven't exceeded max attempts
        if (event.code !== 1000 && reconnectAttempt < WS_CONFIG.MAX_RECONNECT_ATTEMPTS) {
          const timeout = Math.min(
            WS_CONFIG.RECONNECT_INTERVAL * Math.pow(2, reconnectAttempt),
            30000
          );
          console.log(`🔄 Reconnecting in ${timeout}ms... (attempt ${reconnectAttempt + 1})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setReconnectAttempt(prev => prev + 1);
              connect();
            }
          }, timeout);
        } else if (event.code === 1000) {
          console.log('✅ Connection closed normally');
        }
      };

    } catch (error) {
      console.error('❌ Error creating WebSocket connection:', error);
      setIsConnected(false);
    }
  }, [reconnectAttempt]);

  useEffect(() => {
    mountedRef.current = true;
    
    console.log('🚀 Initializing WebSocket connection');
    connect();

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up WebSocket connection');
      mountedRef.current = false;
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    console.warn('⚠️ WebSocket is not connected');
    return false;
  }, []);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    reconnectAttempt,
  };
};

export default useAlertWebSocket;
